import React, { useMemo, useRef, useState } from "react";
import { createSafeContext } from "~/contexts";
import type { PointerLocation } from "../../hooks";
import { usePointerLocation } from "../../hooks";

const [useTooltipSynchronizationContext, TooltipSynchronizationContext] =
  createSafeContext<{
    enabled: boolean;
    setEnabled: React.Dispatch<React.SetStateAction<boolean>>;
    timestamp: bigint | null;
    setTimestamp: React.Dispatch<React.SetStateAction<bigint | null>>;
  }>("TooltipSynchronization");

export function TooltipSynchronizationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [enabled, setEnabled] = useState(false);

  const [_timestamp, setTimestamp] = useState<bigint | null>(null);

  // To simplify downstream code, when tooltip synchronization is disabled,
  // keep letting whatever chart may be tracking the pointer update the
  // timestamp state but always pass `null` down as the value to use. Because
  // the context value is memoized, other charts consuming the context shouldn't
  // need to re-render.
  const timestamp = enabled ? _timestamp : null;

  const value = useMemo(
    () => ({
      enabled,
      setEnabled,
      timestamp,
      setTimestamp,
    }),
    [enabled, setEnabled, timestamp, setTimestamp],
  );

  return (
    <TooltipSynchronizationContext.Provider value={value}>
      {children}
    </TooltipSynchronizationContext.Provider>
  );
}

export function useTooltipSynchronizationSetting(): {
  value: boolean;
  toggle: () => void;
} {
  const { enabled, setEnabled } = useTooltipSynchronizationContext();

  return {
    value: enabled,
    toggle() {
      setEnabled(!enabled);
    },
  };
}

export interface TooltipSynchronizationActions {
  convertLocationToTimestamp: (
    pointerLocation: PointerLocation,
  ) => bigint | null;
}

const [useLocalTooltipSynchronization, LocalTooltipSynchronizationContext] =
  createSafeContext<{
    actionsRef: React.RefObject<TooltipSynchronizationActions>;
    tooltipTimestamp: bigint | null;
    pointerLocation: PointerLocation | null;
  }>("LocalTooltipSynchronization");

/**
 * Provides tooltip synchronization data with additional data scoped to a
 * single panel. Also acts as a container for pointer events necessary to
 * implement tooltip synchronization.
 *
 * Deep dive for future reference:
 *
 * To implement tooltip synchronization (and even just tooltip display for a
 * single chart), we need to listen to pointer events so we can store the
 * pointer location for positioning the tooltip.
 *
 * Initially I wanted to render a transparent <rect> over the chart's drawing
 * area so we'd only listen to pointer events precisely where they're needed
 * and not places like the axes. That doesn't work well with letting users click
 * data points to seek since the transparent <rect> will block the pointer from
 * being able to click anything below it.
 *
 * Next was to try putting the <rect> below everything else in the drawing area
 * so the user could click data points and we could still track the pointer for
 * the tooltip. That doesn't work either because hovering a data point prevents
 * us from being able to continue tracking the pointer for the tooltip when the
 * listeners are attached directly to the <rect>.
 *
 * The next strategy was to attach the pointer listeners to a <g> element that
 * wraps the drawing area so the user can click data points AND have the
 * pointer's position bubble up to the <g>. However, you can't define dimensions
 * for the <g> element: they take on their dimensions from their children.
 * Because we render some points outside the drawing area and clip them (so the
 * line chart continues up to the end of the drawing area), there's no guarantee
 * the <g>'s bounding box matches the drawing area's dimensions, so the position
 * calculated by `usePointerLocation` won't be workable, and I don't currently
 * want to add more complexity to that hook. I'm also finding the intersection
 * of SVGs, pointer events, and bounding boxes seems to be underspecified so
 * there doesn't seem to be clear behavior to expect between browsers.
 *
 * This leads us to needing to put the listeners on an element whose dimensions
 * we can precisely control. The chart's <svg> is an option, but that would
 * require passing props to MUI's <ChartContainer /> component which has to be
 * carefully memoized or you'll end up with long re-renders for essentially
 * unrelated changes. Instead, I decided to wrap the <ChartContainer /> in a
 * <div> of the same dimensions and put all the event listeners on it.
 *
 * The reason for using context is so we can pass the necessary information to
 * our tooltip component without needing to re-render the <ChartContainer /> in
 * between (due to the performance issues mentioned above). The tooltip needs
 * to be rendered below the <ChartContainer /> so it can access some of its
 * context, like the drawing area and the <svg> ref. By accepting the
 * <ChartContainer /> as the `children` prop, we can rely on React not
 * re-rendering it when this provider's internal state changes while still
 * updating our tooltip component further down the tree because of context.
 *
 * To store the timestamp represented by the pointer's position - the crucial
 * data for actually synchronizing tooltips with other charts - we need to
 * use the chart's underlying D3.js scale to convert from the pointer coordinate
 * to a timestamp. Since the scale is only available to children of the
 * <ChartContainer />, we have to pass a ref down for our tooltip component
 * to attach a helper method to so we can calculate the timestamp in the
 * provider, above the <ChartContainer />, while still being able to access the
 * scale.
 */
export function LocalTooltipSynchronizationProvider({
  width,
  height,
  children,
}: {
  width: number;
  height: number;
  children: React.ReactNode;
}) {
  const { timestamp, setTimestamp } = useTooltipSynchronizationContext();

  const actionsRef = useRef<TooltipSynchronizationActions>(null);

  const [pointerLocation, setPointerLocation] =
    useState<PointerLocation | null>(null);

  const pointerContainerProps = usePointerLocation({
    trigger: "pointerenter",
    onChange(location) {
      if (actionsRef.current == null) {
        return;
      }

      setPointerLocation(location);
      setTimestamp(actionsRef.current.convertLocationToTimestamp(location));
    },
    onInteractionEnd() {
      setPointerLocation(null);
      setTimestamp(null);
    },
  });

  return (
    <div style={{ width, height }} {...pointerContainerProps}>
      <LocalTooltipSynchronizationContext.Provider
        value={{ actionsRef, tooltipTimestamp: timestamp, pointerLocation }}
      >
        {children}
      </LocalTooltipSynchronizationContext.Provider>
    </div>
  );
}

export { useLocalTooltipSynchronization as useTooltipSynchronization };
